// src/redux/slices/masterLogoSlice.js
"use client";

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { resetStore } from "./resetStore";
import { getServerUrl, getAuthHeaders } from "../../Utils/serverSelector";

// ===== helpers (JWT + cookies) =====
const stripContentType = (h = {}) => {
  const copy = { ...h };

  if (copy["Content-Type"]) delete copy["Content-Type"];
  if (copy["content-type"]) delete copy["content-type"];

  if (copy.common) {
    delete copy.common["Content-Type"];
    delete copy.common["content-type"];
  }
  if (copy.post) {
    delete copy.post["Content-Type"];
    delete copy.post["content-type"];
  }

  return copy;
};

const authJsonConfig = async () => {
  const headers = await getAuthHeaders();
  return {
    headers,
    withCredentials: true,
    validateStatus: () => true, // сами обработаем статус
  };
};

const authMultipartConfig = async () => {
  const headers = stripContentType(await getAuthHeaders());
  return {
    headers,
    withCredentials: true,
    validateStatus: () => true,
  };
};

// ===== Получить логотип мастера (приватный) =====
export const fetchMasterLogo = createAsyncThunk(
  "masterLogo/fetchMasterLogo",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID not provided");

      const cfg = await authJsonConfig();
      const response = await axios.get(
        `${getServerUrl()}/master-logo/${uid}/logo`,
        cfg
      );

      // нет логотипа — норм
      if (response.status === 404) {
        return null;
      }

      // отказ в доступе — считаем ошибкой авторизации
      if (response.status === 403) {
        throw new Error(
          response.data?.message ||
            "Forbidden (403) while fetching master logo"
        );
      }

      if (response.status !== 200) {
        throw new Error(
          response.data?.message ||
            `Failed to fetch master logo (HTTP ${response.status})`
        );
      }

      // ожидаем { filePath: "/path/to/logo.png" }
      return response.data?.filePath ?? null;
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data ||
        error?.message ||
        "Failed to fetch master logo";
      return rejectWithValue(msg);
    }
  }
);

// ===== Загрузить логотип мастера (приватный) =====
export const uploadMasterLogo = createAsyncThunk(
  "masterLogo/uploadMasterLogo",
  async ({ uid, file }, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID not provided");
      if (!file) throw new Error("File not provided");

      const formData = new FormData();
      formData.append("uid", uid);
      formData.append("file", file); // File/Blob из <input type="file" />

      const cfg = await authMultipartConfig();
      const response = await axios.post(
        `${getServerUrl()}/master-logo`,
        formData,
        cfg
      );

      if (response.status !== 200 && response.status !== 201) {
        throw new Error(
          response.data?.message ||
            `Failed to upload master logo (HTTP ${response.status})`
        );
      }

      // ожидаем { filePath: "/path/to/logo.png" }
      return response.data?.filePath ?? null;
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data ||
        error?.message ||
        "Failed to upload master logo";
      return rejectWithValue(msg);
    }
  }
);

// ===== Удалить логотип мастера (приватный) =====
export const deleteMasterLogo = createAsyncThunk(
  "masterLogo/deleteMasterLogo",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID not provided");

      const cfg = await authJsonConfig();
      const response = await axios.delete(
        `${getServerUrl()}/master-logo/${uid}/logo`,
        cfg
      );

      if (response.status !== 200) {
        throw new Error(
          response.data?.message ||
            `Failed to delete master logo (HTTP ${response.status})`
        );
      }

      return null; // логотип удалён
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data ||
        error?.message ||
        "Failed to delete master logo";
      return rejectWithValue(msg);
    }
  }
);

// ===== Публичный логотип мастера (без авторизации) =====
export const fetchPublicMasterLogo = createAsyncThunk(
  "masterLogo/fetchPublicMasterLogo",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID not provided");
      const base = (getServerUrl() || "").replace(/\/$/, "");

      const { data } = await axios.get(
        `${base}/public/${uid}/master-logo`,
        { validateStatus: () => true }
      );

      if (data == null) return null;

      // ожидаем { filePath: "/uploads/.../logo.png" } или null
      return data?.filePath ?? null;
    } catch (e) {
      if (e?.response?.status === 404) return null;
      return rejectWithValue(
        e?.response?.data ||
          e?.message ||
          "Failed to fetch public master logo"
      );
    }
  }
);

const initialState = {
  logoUrl: null, // относительный или абсолютный путь
  status: "idle", // idle | loading | failed
  error: null,
};

const masterLogoSlice = createSlice({
  name: "masterLogo",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fetch (приватный)
      .addCase(fetchMasterLogo.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchMasterLogo.fulfilled, (state, action) => {
        state.status = "idle";
        state.logoUrl = action.payload; // может быть null
      })
      .addCase(fetchMasterLogo.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload || action.error?.message || "error";
      })

      // upload
      .addCase(uploadMasterLogo.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(uploadMasterLogo.fulfilled, (state, action) => {
        state.status = "idle";
        state.logoUrl = action.payload; // filePath или null
      })
      .addCase(uploadMasterLogo.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload || action.error?.message || "error";
      })

      // delete
      .addCase(deleteMasterLogo.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(deleteMasterLogo.fulfilled, (state) => {
        state.status = "idle";
        state.logoUrl = null;
      })
      .addCase(deleteMasterLogo.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload || action.error?.message || "error";
      })

      // public
      .addCase(fetchPublicMasterLogo.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchPublicMasterLogo.fulfilled, (state, action) => {
        state.status = "idle";

        const base = (getServerUrl() || "").replace(/\/$/, "");
        const fp = action.payload;

        const abs = fp
          ? /^https?:\/\//i.test(fp)
            ? fp
            : `${base}/${String(fp).replace(/^\//, "")}`
          : null;

        // cache-busting
        state.logoUrl = abs
          ? `${abs}${abs.includes("?") ? "&" : "?"}t=${Date.now()}`
          : null;
      })
      .addCase(fetchPublicMasterLogo.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload || action.error?.message || "error";
      })

      // reset
      .addCase(resetStore, () => initialState);
  },
});

export default masterLogoSlice.reducer;
